import React, { useState, useMemo } from 'react';

import classnames from 'classnames';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { usePaywallContext } from 'components/Paywall';

import { TP } from 'constants/index';

import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import { useDialogs } from 'utils/hooks/useDialogs';
import usePageContext from 'utils/hooks/usePageContext';
import { useQuery } from 'utils/react-query';

import orgQueries from 'containers/Organizations/queries';

import { useTranslation } from 'src/i18n';

import classes from './RentalBanner.module.scss';

const MAP_ICONS = {
  1: 'domino_mask',
  2: 'festival',
  3: 'piano',
};

const ContactDetails = ({ rentalContacts, showAll }) => {
  const { isMobile } = useDeviceTypeLayouts();
  const allContacts = rentalContacts?.data;
  const contactList = allContacts?.slice(0, showAll ? allContacts.length : 1);

  return (
    <div className={classes.contactList}>
      {contactList?.map(contact => (
        <div key={contact?.id} className={classes.contact}>
          {contact?.profile?.user?.name?.length > 0 && (
            <div className={classes.contactName}>
              <Typography weight="medium" size={isMobile ? 12 : 14}>
                {contact?.profile?.user?.name}
                {contact?.roles?.[0]?.profession?.name && (
                  <Typography weight="regular" className={classes.contactRole}>
                    ({contact.roles[0].profession.name})
                  </Typography>
                )}
              </Typography>
            </div>
          )}
          {contact?.workPhones?.length > 0 && (
            <div className={classes.contactPhones}>
              <SpriteIcon icon="call" size={12} className={classes.phoneIcon} />
              <Typography weight="regular" size={isMobile ? 12 : 14} inline>
                {contact.workPhones.map(({ phone }) => phone)?.join?.(', ')}
              </Typography>
            </div>
          )}
          {contact?.workEmail && (
            <div className={classes.contactEmails}>
              <SpriteIcon icon="mail" size={12} className={classes.emailIcon} />
              <Typography weight="regular" size={isMobile ? 12 : 14} inline>
                {contact.workEmail?.email}
              </Typography>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const RentalBanner = ({ entity, isQuickView, styles, isFeaturedCard, isFullBanner }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isMobile } = useDeviceTypeLayouts();
  const { permissions } = usePageContext();
  const { isLoggedIn } = permissions;
  const { setIsPaywallExpanded } = usePaywallContext();
  const { data: rentalContacts } = useQuery(
    orgQueries.getProductionRentalContacts({ entity, queryConfig: { enabled: !isFeaturedCard && !isFullBanner } }),
  );
  const { setIsLoginDialog } = useDialogs();
  const [isViewMoreExpand, setIsViewMoreExpand] = useState(false);

  const isArchived = entity?.isArchived;

  const { rentalIncludes, isActive } = useMemo(() => {
    const rentalEntity = entity?.rentals?.find(rental => rental?.is_active && rental?.rentalIncludes?.length > 0);

    return {
      rentalIncludes: rentalEntity?.rentalIncludes,
      isActive: entity?.rentals?.some(rental => rental?.is_active),
    };
  }, [entity?.rentals]);

  if (!isActive) {
    return null;
  }

  if (isFullBanner) {
    return (
      <div className={classes.rentalBannerFullBanner}>
        <div className={classes.rentalBannerFullBanner__content}>
          <Typography size={11} weight="medium">
            {t(`${TP}.RENTAL_AVAILABLE`)}
          </Typography>
          {rentalIncludes?.length > 0 && (
            <span className={classes.rentalBannerFullBanner__items}>
              {rentalIncludes?.map((item, index) => (
                <span className={classes.rentalBannerFullBanner__items_item} key={index}>
                  <Typography
                    size={10}
                    className={classes.text}
                    blur={isArchived}
                    {...(isArchived && {
                      onClick: e => {
                        e.stopPropagation();
                        e.preventDefault();
                        setIsPaywallExpanded(true);
                      },
                    })}
                  >
                    {item?.rentalIncludeType?.name}
                  </Typography>
                </span>
              ))}
            </span>
          )}
        </div>
        <div className={classes.rentalBannerFullBanner__contactInfo}>
          <Typography size={10} className={classes.text}>
            {t(`${TP}.FN_VIEW_CONTACT_DETAILS`)}
            <SpriteIcon icon="chevron_right" size={14} className={classes.arrowIcon} />
          </Typography>
        </div>
      </div>
    );
  }

  if (isFeaturedCard) {
    return (
      <div className={classes.rentalBannerFeaturedCard}>
        <Typography size={11} weight="medium">
          {t(`${TP}.RENTAL_AVAILABLE`)}
        </Typography>
        <div className={classes.rentalBannerFeaturedCard__rightWrapper}>
          <div className={classes.rentalBannerFeaturedCard__rightWrapper_content}>
            <div className={classes.rentalBannerFeaturedCard__rightWrapper_content_items}>
              {rentalIncludes?.map((item, index) => (
                <Typography size={10} className={classes.item} key={index} blur={isArchived}>
                  {item?.rentalIncludeType?.name}
                </Typography>
              ))}
            </div>
            <Typography size={10} className={classes.text}>
              {t(`${TP}.FN_VIEW_CONTACT_DETAILS`)}
            </Typography>
          </div>
          <div>
            <SpriteIcon icon="chevron_right" size={14} className={classes.arrowIcon} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classnames(classes.rentalBanner, {
        [classes.rentalBannerQuickView]: isQuickView,
        [styles?.rentalBanner]: !!styles?.rentalBanner,
      })}
    >
      <div className={classes.rentalBanner__primaryText}>
        <div className={classes.rentalBanner__primaryText_title}>
          <SpriteIcon icon="movie_edit" size={isMobile ? 14 : 16} className={classes.movieClapIcon} />
          <Typography size={isMobile ? 14 : 16} weight="medium">
            {t(`RENTAL_CO_PRODUCTION`)}
          </Typography>
        </div>
        {rentalIncludes?.length > 0 && (
          <span className={classes.rentalBanner__items}>
            {rentalIncludes?.map((item, index) => (
              <span className={classes.rentalBanner__items_item} key={index}>
                <SpriteIcon
                  icon={MAP_ICONS[item?.rentalIncludeType?.id]}
                  size={isMobile ? 12 : 14}
                  className={classes.icon}
                />
                <Typography size={isMobile ? 12 : 14} className={classes.text}>
                  {item?.rentalIncludeType?.name}
                </Typography>
              </span>
            ))}
          </span>
        )}
      </div>
      {rentalContacts?.data?.length > 0 && (
        <div className={classes.rentalBanner__contactInfo}>
          {isLoggedIn ? (
            <ContactDetails rentalContacts={rentalContacts} showAll={isViewMoreExpand} />
          ) : (
            <LinkButton
              rightIcon={<SpriteIcon icon="chevron_right" size={14} className={classes.arrowIcon} />}
              variant="text"
              styles={{ root: classes.viewContact }}
              onClick={() => setIsLoginDialog({ isOpen: true })}
            >
              {t(`${TP}.FN_LOGIN_VIEW_CONTACT_DETAILS`)}
            </LinkButton>
          )}
          {rentalContacts?.data?.length > 1 && isLoggedIn && (
            <LinkButton
              rightIcon={
                <SpriteIcon
                  icon={isViewMoreExpand ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                  size={14}
                  className={classes.arrowIcon}
                />
              }
              variant="text"
              styles={{ root: classes.viewMoreBtn }}
              onClick={() => setIsViewMoreExpand(!isViewMoreExpand)}
            >
              {isViewMoreExpand ? t(`${TP}.FN_VIEW_LESS`) : t(`${TP}.FN_VIEW_MORE`)}
            </LinkButton>
          )}
        </div>
      )}
    </div>
  );
};

export default RentalBanner;
